import React, { useState, useEffect, useCallback, useContext } from 'react'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'
import PropTypes from 'prop-types'
import classnames from 'classnames'

// hooks
import { useBuyerSettings } from '../../../../hooks/buyerSettings'
import { useBranding } from '../../../../hooks/branding'
import { useMessageGroupRequestAsync } from '../../../../hooks/requests/directMessages'
import { usePrevious } from '../../../../hooks/utility'
import { useToggleGuestSignup } from '../../../../hooks/routing'

// components
import { If, Unless, Drawer, MultiSelect } from '../../../../components'
import CospoPrequalPrompt from '../CospoPrequalPrompt'

//constants
import { GUEST_CLIENT_TYPE } from '../../../../constants/buyerSettings'
import {
  CUSTOM_QUESTION_TOPIC_KEY,
  PREQUAL_TOPIC_KEY,
} from '../../../../constants/directMessages'

//JSON
import questions from './FooterQuestions'

// Styles
import styles from './Footer.module.scss'

const Footer = ({ className }) => {
  const { ampli } = useContext(AmplitudeContext)
  const { clientType } = useBuyerSettings()
  const toggleSignup = useToggleGuestSignup()

  ////Drawer////
  const [drawerText, setDrawerText] = useState('')
  const [drawerSubText, setDrawerSubText] = useState('')
  const [drawerExpanded, setDrawerExpanded] = useState(false)

  ////MultiSelect////
  const [customQuestionMessage, setCustomQuestionMessage] = useState('')
  const [selectedQuestions, setSelectedQuestions] = useState([])

  ////PrequalPrompt////
  const [shouldPromptPrequal, setShouldPromptPrequal] = useState(false)
  const [prequalSelected, setPrequalSelected] = useState(false)
  const [showPrequal, setShowPrequal] = useState(false)

  const { customerFirstName, customerPhotoUri, isLoading, isSponsorPresent } =
    useBranding()

  const {
    execute,
    reset,
    executeStatus: dmStatus,
    isLoading: dmLoading,
    isErrored: dmErrored,
    isSuccess: dmSuccess,
  } = useMessageGroupRequestAsync()

  const messageSuccessText = 'Message Sent!'
  const messageSuccessSubText = `${customerFirstName} will be in touch soon`

  const initializedText = 'Have any questions?'
  const initializedSubText = `${customerFirstName}'s here to help`

  const handleSuccessText = useCallback(() => {
    setDrawerText(messageSuccessText)
    setDrawerSubText(messageSuccessSubText)
  }, [messageSuccessText, messageSuccessSubText])

  const handleInitializedText = useCallback(() => {
    setDrawerText(initializedText)
    setDrawerSubText(initializedSubText)
  }, [initializedText, initializedSubText])

  const handleCustomQuestion = e => {
    const value = e.target.value
    setCustomQuestionMessage(value)
  }

  const handleToggleDrawer = useCallback(() => {
    if (drawerExpanded) {
      setSelectedQuestions([])
      setCustomQuestionMessage('')
    }

    if (clientType === GUEST_CLIENT_TYPE) {
      toggleSignup({
        expandModal: 1,
      })
    } else {
      setDrawerExpanded(!drawerExpanded)
    }
  }, [drawerExpanded, clientType, toggleSignup])

  const updateSelectedQuestions = useCallback(questions => {
    setSelectedQuestions(questions)
  }, [])

  const executeResetTimeout = useCallback(() => {
    const timer = setTimeout(() => {
      handleInitializedText()
      reset()
    }, 4000)
    return () => {
      clearTimeout(timer)
    }
  }, [handleInitializedText, reset])

  const handleCospoPrequalPromptClose = useCallback(() => {
    setPrequalSelected(false)
    setShowPrequal(false)
    executeResetTimeout()
  }, [executeResetTimeout])

  const onSubmit = useCallback(() => {
    if (customQuestionMessage) {
      selectedQuestions.push({
        topic: 'a Listing they viewed',
        topicKey: CUSTOM_QUESTION_TOPIC_KEY,
        message: customQuestionMessage,
      })
    }

    let questionsToSubmit = [...selectedQuestions]

    if (prequalSelected) {
      ampli?.clientRequestedInfo({
        request_type: AmplitudeEventConstants.CLIENT_REQUESTED_PREQUAL,
      })

      if (isSponsorPresent) {
        questionsToSubmit = selectedQuestions.filter(
          question => question.topicKey !== PREQUAL_TOPIC_KEY
        )

        if (questionsToSubmit.length > 0) {
          // if there are more questions, wait for first request to fire
          setShouldPromptPrequal(true)
        } else {
          // else the regular request will not fire, so go ahead and prompt
          setShowPrequal(true)
          handleToggleDrawer()
        }
      }
    }

    if (questionsToSubmit.length > 0) {
      execute(questionsToSubmit)
    }

    setCustomQuestionMessage('')
    setSelectedQuestions([])
  }, [
    customQuestionMessage,
    execute,
    handleToggleDrawer,
    isSponsorPresent,
    prequalSelected,
    selectedQuestions,
  ])

  const previousStatus = usePrevious(dmStatus)
  useEffect(() => {
    if (previousStatus !== dmStatus) {
      if (dmSuccess) {
        handleToggleDrawer()
        handleSuccessText()

        if (shouldPromptPrequal) {
          setShowPrequal(true)
        } else {
          executeResetTimeout()
        }
      }
    }
  }, [
    prequalSelected,
    dmStatus,
    dmErrored,
    dmSuccess,
    setDrawerExpanded,
    handleSuccessText,
    handleToggleDrawer,
    isSponsorPresent,
    previousStatus,
    executeResetTimeout,
    shouldPromptPrequal,
  ])

  useEffect(() => {
    handleInitializedText()
  }, [handleInitializedText])

  const numberOfMessages = customQuestionMessage
    ? selectedQuestions.length + 1
    : selectedQuestions.length

  const buttonLabel =
    numberOfMessages > 1
      ? `Send ${numberOfMessages} Questions`
      : `Send Question`

  const multiSelectDisabled =
    !selectedQuestions.length && !customQuestionMessage

  return (
    <div className={classnames(styles.footer, className)}>
      <Unless condition={isLoading}>
        <Drawer
          onClick={handleToggleDrawer}
          imageSrc={customerPhotoUri}
          text={drawerText}
          subtext={drawerSubText}
          status={dmStatus}
          expanded={drawerExpanded}
          disabled={dmSuccess}
          children={
            <MultiSelect
              onCustomQuestionChange={handleCustomQuestion}
              updateSelectedQuestions={updateSelectedQuestions}
              setPrequalSelected={setPrequalSelected}
              onSubmit={onSubmit}
              customQuestionMessage={customQuestionMessage}
              multiSelectDisabled={multiSelectDisabled}
              buttonLabel={buttonLabel}
              questions={questions}
              loadingSpin={dmLoading}
              error={dmErrored}
            />
          }
        ></Drawer>
      </Unless>
      <If condition={showPrequal}>
        <CospoPrequalPrompt onClose={handleCospoPrequalPromptClose} />
      </If>
    </div>
  )
}

Footer.propTypes = {
  className: PropTypes.string,
}

export default Footer
